import { faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { faFileImport } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import TlsService from '../../service/TlsService'
import GatewayType from '../GatewayType'
import KeyStoreImport from './KeyStoreImport'
import { expandRowFunction } from '../../../gateway/utils/DatatableUtil'

const EncryptionKeys: GatewayType = () => {
  const { t } = useTranslation()
  const [keysWithCerts, setKeysWithCerts] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { confirmDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await TlsService.getTlsKeysWithCerts(setKeysWithCerts)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('keys.name'),
        dataField: 'name',
        sort: true,
      },
      {
        text: t('keys.keyId'),
        dataField: 'keyId',
        sort: true,
      },
      {
        text: t('keys.algorithm'),
        dataField: 'algorithm',
        sort: true,
      },
      {
        text: t('keys.version'),
        dataField: 'keyVersion',
        sort: true,
      },
      {
        text: t('action'),
        dataField: 'df1',
        isDummyField: true,
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.keystore.keys.delete}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteTlsKey(row, setKeysWithCerts, t, confirmDialogPropsSetter)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter])

  const expandRow = expandRowFunction(renderer, () => { })

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={keysWithCerts}
        columns={columns}
        caption={t('keys.tlsKeys')}
        expandRow={expandRow}
        captionGridSize={5}
        captionButtons={[
          {
            onClick: () => importKeystore(),
            text: t('import'),
            icon: faFileImport,
            permissions: Authorization.keystore.keys.edit,
          },
        ]}
        helpLink={t('keys.helpLink')}
      />
    </Loading>
  )

  function importKeystore() {
    const dialogProps: ConfirmDialogProps = {
      show: true,
      header: t('keys.importKeystoreHeader'),
      body: <KeyStoreImport setKeys={keysWithCerts} />,
      showFooter: false,
    }
    confirmDialogPropsSetter(dialogProps)
  }

  function renderer(row: any, rowIndex: any, ...args: any[]) {
    const data: any[] = row.certs

    const columns: any[] = [
      {
        text: t('certificates.subject'),
        dataField: 'subjectDn',
        sort: true,
      },
      {
        text: t('certificates.issuer'),
        dataField: 'issuerDn',
        sort: true,
      },
      {
        text: t('certificates.notBefore'),
        dataField: 'notBefore',
        formatter: (cell: any) => new Date(cell).toString(),
        sort: true,
      },
      {
        text: t('certificates.notAfter'),
        dataField: 'notAfter',
        formatter: (cell: any) => new Date(cell).toString(),
        sort: true,
      },
    ]

    if (!data.length) {
      return <React.Fragment key={row.id} />
    }
    return (
      <React.Fragment key={row.id}>
        <DataTable
          data={data}
          columns={columns}
          searchable={false}
          striped={false}
          hover={false}
          condensed={true}
        />
      </React.Fragment>
    )
  }
}


function deleteTlsKey(row: any, setKeysWithCerts: React.Dispatch<any>, t: TFunction, confirmDialogPropsSetter: any) {
  const editDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('keys.deleteTlsKeyHeader'),
    body: (
      <>
        {t('keys.deleteTlsKeyBody')
          .split('\n')
          .map((item) => (
            <p>{item}</p>
          ))}
      </>
    ),
    submitText: 'Delete',
    onSubmit: TlsService.deleteTlsKey,
    onSubmitExtraArgs: [row.keyId, row.keyVersion, setKeysWithCerts, t],
  }
  confirmDialogPropsSetter(editDialogProps)
}

export default EncryptionKeys
